"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { Clock, Mail, MapPin, ShieldCheck, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, ArrowRight, FlaskConical } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  default?: string | string[];
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  config?: Record<string, any>;
  validation?: {
    messages?: Record<string, string>;
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description?: string;
  availability: {
    accepting: boolean;
    state: string;
    message?: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label?: string;
  };
  spam: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds: number;
    challenge?: any;
  };
}
export default function Inquiry() {
  const shouldReduceMotion = useReducedMotion();

  // Form definition state
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "error">("loading");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);

  // Form interaction state
  const [values, setValues] = useState<Record<string, string>>({});
  const [honeypotVal, setHoneypotVal] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);

  // Fetch form definition on mount
  const fetchDefinition = useCallback(async () => {
    setLoadStatus("loading");
    setFormError(null);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        setLoadStatus("error");
        return;
      }

      // Try designated key 'contact_form'
      let res: FormDefinition;
      try {
        res = await client.pluginRequest("wvc-forms/v1/forms/contact_form");
      } catch (err: any) {
        if (err?.status === 404) {
          // Recovery: list forms and find matching one
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items: Array<{
            key: string;
            name: string;
          }> = listRes?.items || [];
          const matched = items.find(f => f.key === "contact_form") || (items.length === 1 ? items[0] : null) || items.find(f => f.name.toLowerCase().includes("contact") || f.name.toLowerCase().includes("inquiry"));
          if (matched) {
            res = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          } else {
            setLoadStatus("error");
            return;
          }
        } else {
          setLoadStatus("error");
          return;
        }
      }
      if (res && res.fields) {
        setFormDef(res);
        setFetchTimestamp(Date.now());

        // Initialize defaults
        const initialVals: Record<string, string> = {};
        res.fields.forEach(fld => {
          if (typeof fld.default === "string") {
            initialVals[fld.id] = fld.default;
          } else {
            initialVals[fld.id] = "";
          }
        });
        setValues(initialVals);
        setLoadStatus("ready");
      } else {
        setLoadStatus("error");
      }
    } catch {
      setLoadStatus("error");
    }
  }, []);
  useEffect(() => {
    fetchDefinition();
  }, [fetchDefinition]);
  const handleInputChange = (fieldId: string, val: string) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const copy = {
          ...prev
        };
        delete copy[fieldId];
        return copy;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;

    // Enforce spam.min_seconds rule to prevent silent quarantine
    const minMs = (formDef.spam.min_seconds || 3) * 1000;
    const elapsed = Date.now() - fetchTimestamp;
    if (elapsed < minMs) {
      const waitTime = minMs - elapsed;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    setIsSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        setFormError("Submission service currently unavailable. Please try again shortly.");
        setIsSubmitting(false);
        return;
      }
      const postData: any = {
        values,
        token: formDef.spam.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam.honeypot) {
        postData.hp = {
          [formDef.spam.honeypot.field]: honeypotVal
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: postData
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else if (res.confirmation?.message) {
          setConfirmationHtml(res.confirmation.message);
        } else {
          setConfirmationHtml("<p>Your inquiry has been received. Our scientific advisory desk will review your specifications shortly.</p>");
        }
      } else if (res?.code === "frms_validation_failed" && res?.errors) {
        setFieldErrors(res.errors);
        setFormError(res.message || "Please verify the highlighted fields.");
      } else if (res?.code === "frms_invalid_token") {
        setFormError("Session expired. Refreshing verification token...");
        await fetchDefinition();
      } else {
        setFormError(res?.message || "Unable to submit inquiry. Please review your entries.");
      }
    } catch (err: any) {
      setFormError("A network error occurred. Please verify your connection and retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Find fields by key or fallback position to ensure robust mapping
  const findField = (keyName: string, indexFallback: number): FormFieldDefinition | undefined => {
    if (!formDef?.fields) return undefined;
    const found = formDef.fields.find(f => f.key === keyName);
    if (found) return found;
    return formDef.fields[indexFallback];
  };
  const nameField = findField("name", 0) || findField("full_name", 0);
  const emailField = findField("email", 1) || findField("institutional_email", 1);
  const subjectField = findField("subject", 2) || findField("topic", 2);
  const messageField = findField("message", 3) || findField("inquiry_message", 3);

  // Animations configuration (clean clinical rise)
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="contact-section" data-nav="light" className="bg-background text-foreground py-14 md:py-20 border-b border-border">
      <div className="mx-auto w-full max-w-[1088px] px-6 md:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Scientific Advisory Credentials & Details */}
          <motion.div variants={itemVariants} className="lg:col-span-5 space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="h-px w-6 bg-primary" />
                <span className="text-xs font-bold tracking-[0.1em] text-primary uppercase">
                  SCIENTIFIC ADVISORY
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground font-default">
                Direct Laboratory Inquiries
              </h2>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Connect with our scientific advisory desk in India for batch documentation, custom synthesis protocols, and institutional research supply.
              </p>
            </div>

            {/* Structured Communication Points */}
            <div className="space-y-4 pt-2 border-t border-border">
              {/* Direct Support Email */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-muted text-primary shrink-0 border border-border">
                  <Mail className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Direct Laboratory Email
                  </p>
                  <a href="mailto:support@researchpeptide.in" className="text-sm font-medium text-foreground hover:text-primary transition-colors inline-block mt-0.5">
                    support@researchpeptide.in
                  </a>
                </div>
              </div>

              {/* Physical Dispatch Hub */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-muted text-primary shrink-0 border border-border">
                  <MapPin className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Dispatch Hub & Cold-Chain Center
                  </p>
                  <p className="text-sm font-medium text-foreground mt-0.5">
                    India (specific address to be added by client)
                  </p>
                </div>
              </div>

              {/* Operating Schedule */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-muted text-primary shrink-0 border border-border">
                  <Clock className="size-4" />
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Operating Schedule
                  </p>
                  <p className="text-sm font-medium text-foreground mt-0.5 font-mono">
                    Monday to Friday, 09:00 - 18:00 IST
                  </p>
                </div>
              </div>
            </div>

            {/* Response-Time Reassurance Badge */}
            <div className="p-4 rounded-md bg-muted border border-border space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-background text-primary border-primary/30 font-mono text-[11px] gap-1">
                  <ShieldCheck className="size-3" />
                  SLA GUARANTEE
                </Badge>
                <span className="text-xs font-semibold text-foreground">
                  24-Hour Technical Response
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-normal">
                All technical inquiries are answered within 24 business hours by qualified chemists and peptide synthesis specialists.
              </p>
            </div>

            {/* RUO Regulatory Marker */}
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground pt-1">
              <FlaskConical className="size-3.5 text-primary" />
              <span>Certified Research-Use-Only Reagents (RUO)</span>
            </div>
          </motion.div>

          {/* Right Column: wvc-forms Backed Inquiry Form */}
          <motion.div variants={itemVariants} className="lg:col-span-7 bg-card border border-border rounded-md p-6 sm:p-8 shadow-xs">
            {loadStatus === "loading" && <div className="flex flex-col items-center justify-center py-16 space-y-3">
                <Loader2 className="size-6 text-primary animate-spin" />
                <p className="text-xs font-mono text-muted-foreground">
                  Connecting to secure laboratory dispatch server...
                </p>
              </div>}

            {loadStatus === "error" && <div className="space-y-4 py-8 text-center">
                <AlertCircle className="size-8 text-destructive mx-auto" />
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    Inquiry Desk Offline
                  </h3>
                  <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    The direct inquiry pipeline is temporarily unreachable. Please contact our advisory team directly at support@researchpeptide.in.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDefinition} className="text-xs">
                  Retry Connection
                </Button>
              </div>}

            {loadStatus === "ready" && formDef && <>
                {confirmationHtml ? <div className="space-y-4 py-8 text-center">
                    <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                      <CheckCircle2 className="size-6" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-lg font-semibold text-foreground">
                        Inquiry Received
                      </h3>
                      <div className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto [&>p]:mt-1" dangerouslySetInnerHTML={{
                  __html: confirmationHtml
                }} />
                    </div>
                    <div className="pt-4">
                      <Button variant="outline" size="sm" onClick={() => {
                  setConfirmationHtml(null);
                  setValues({});
                  fetchDefinition();
                }} className="text-xs">
                        Submit Another Inquiry
                      </Button>
                    </div>
                  </div> : <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    {/* Top alert if form closed */}
                    {!formDef.availability?.accepting && <div className="p-3 bg-muted border border-border rounded-md flex items-center gap-2 text-xs text-muted-foreground">
                        <AlertCircle className="size-4 text-destructive shrink-0" />
                        <span>
                          {formDef.availability?.message || "Inquiries are currently closed for scheduled maintenance."}
                        </span>
                      </div>}

                    {/* General Error Banner */}
                    {formError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2 text-xs text-destructive">
                        <AlertCircle className="size-4 shrink-0 mt-0.5" />
                        <span>{formError}</span>
                      </div>}

                    {/* Honeypot Decoy Trap Field (hidden from humans, trapped for bots) */}
                    {formDef.spam?.honeypot && <div style={{
                position: "absolute",
                left: "-9999px",
                opacity: 0,
                pointerEvents: "none"
              }} aria-hidden="true">
                        <label htmlFor={formDef.spam.honeypot.field}>Do not fill this</label>
                        <input id={formDef.spam.honeypot.field} type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} tabIndex={-1} autoComplete="off" />
                      </div>}

                    {/* Clinical Form Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Full Name */}
                      {nameField && <div className="space-y-1.5">
                          <Label htmlFor={nameField.id} className="text-xs font-medium text-foreground flex items-center justify-between">
                            <span>{nameField.label || "Full Name"}</span>
                            {nameField.required && <span className="text-[10px] text-primary font-mono">*</span>}
                          </Label>
                          <Input id={nameField.id} name={nameField.id} type="text" placeholder={nameField.placeholder || "Dr. Rajesh Kumar"} value={values[nameField.id] || ""} onChange={e => handleInputChange(nameField.id, e.target.value)} required={nameField.required} disabled={isSubmitting || !formDef.availability?.accepting} className="h-10 text-sm bg-background border-input focus-visible:ring-primary focus-visible:border-primary" />
                          {fieldErrors[nameField.id] && <p className="text-[11px] text-destructive">
                              {fieldErrors[nameField.id].message}
                            </p>}
                        </div>}

                      {/* Institutional Email */}
                      {emailField && <div className="space-y-1.5">
                          <Label htmlFor={emailField.id} className="text-xs font-medium text-foreground flex items-center justify-between">
                            <span>{emailField.label || "Institutional Email"}</span>
                            {emailField.required && <span className="text-[10px] text-primary font-mono">*</span>}
                          </Label>
                          <Input id={emailField.id} name={emailField.id} type="email" placeholder={emailField.placeholder || "researcher@inst.res.in"} value={values[emailField.id] || ""} onChange={e => handleInputChange(emailField.id, e.target.value)} required={emailField.required} disabled={isSubmitting || !formDef.availability?.accepting} className="h-10 text-sm bg-background border-input focus-visible:ring-primary focus-visible:border-primary font-mono" />
                          {fieldErrors[emailField.id] && <p className="text-[11px] text-destructive">
                              {fieldErrors[emailField.id].message}
                            </p>}
                        </div>}
                    </div>

                    {/* Subject / Topic */}
                    {subjectField && <div className="space-y-1.5">
                        <Label htmlFor={subjectField.id} className="text-xs font-medium text-foreground flex items-center justify-between">
                          <span>{subjectField.label || "Subject / Technical Topic"}</span>
                          {subjectField.required && <span className="text-[10px] text-primary font-mono">*</span>}
                        </Label>
                        <Input id={subjectField.id} name={subjectField.id} type="text" placeholder={subjectField.placeholder || "COA Verification / Custom Synthesis Quote"} value={values[subjectField.id] || ""} onChange={e => handleInputChange(subjectField.id, e.target.value)} required={subjectField.required} disabled={isSubmitting || !formDef.availability?.accepting} className="h-10 text-sm bg-background border-input focus-visible:ring-primary focus-visible:border-primary" />
                        {fieldErrors[subjectField.id] && <p className="text-[11px] text-destructive">
                            {fieldErrors[subjectField.id].message}
                          </p>}
                      </div>}

                    {/* Detailed Inquiry Message */}
                    {messageField && <div className="space-y-1.5">
                        <Label htmlFor={messageField.id} className="text-xs font-medium text-foreground flex items-center justify-between">
                          <span>{messageField.label || "Detailed Inquiry Message"}</span>
                          {messageField.required && <span className="text-[10px] text-primary font-mono">*</span>}
                        </Label>
                        <Textarea id={messageField.id} name={messageField.id} rows={4} placeholder={messageField.placeholder || "Specify peptide sequence, requested purity level (≥98% HPLC), quantity requirements, or target batch lot numbers..."} value={values[messageField.id] || ""} onChange={e => handleInputChange(messageField.id, e.target.value)} required={messageField.required} disabled={isSubmitting || !formDef.availability?.accepting} className="text-sm bg-background border-input focus-visible:ring-primary focus-visible:border-primary min-h-[100px]" />
                        {fieldErrors[messageField.id] && <p className="text-[11px] text-destructive">
                            {fieldErrors[messageField.id].message}
                          </p>}
                      </div>}

                    {/* Submit Button Action */}
                    <div className="pt-2">
                      <Button type="submit" disabled={isSubmitting || !formDef.availability?.accepting} className="w-full sm:w-auto px-7 py-3 h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm rounded-md tracking-[0.02em] shadow-xs transition-colors">
                        {isSubmitting ? <>
                            <Loader2 className="size-4 animate-spin mr-2" />
                            <span>Transmitting Inquiry...</span>
                          </> : <>
                            <span>{formDef.submit?.label || "Submit Inquiry"}</span>
                            <ArrowRight className="size-4 ml-1.5" />
                          </>}
                      </Button>
                    </div>

                    <p className="text-[11px] text-muted-foreground leading-tight pt-1">
                      Submitted analytical inquiries are handled under strict confidentiality protocols. Data is transmitted directly to our laboratory synthesis desk.
                    </p>
                  </form>}
              </>}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}